/**
 * Grid / list switch for report feeds. The choice is remembered per browser
 * (localStorage `smc_report_view`) and shared by every report list in the app.
 */
import { useEffect, useState } from 'react';

const KEY = 'smc_report_view';
const EVT = 'smc-report-view';

function read() {
  try { return localStorage.getItem(KEY) === 'list' ? 'list' : 'grid'; } catch (_) { return 'grid'; }
}

export function useReportView() {
  const [view, setViewState] = useState(read);
  useEffect(() => {
    const sync = () => setViewState(read());
    window.addEventListener(EVT, sync);
    return () => window.removeEventListener(EVT, sync);
  }, []);
  const setView = v => {
    try { localStorage.setItem(KEY, v); } catch (_) { /* private mode */ }
    setViewState(v);
    window.dispatchEvent(new Event(EVT));
  };
  return [view, setView];
}

/**
 * Grid template for the current view: responsive columns (3 desktop / 2 tablet
 * / 1 phone, from --smc-cols in v2.css), or one full-width column.
 */
export const reportGridColumns = view => (view === 'list' ? '1fr' : 'var(--smc-cols, repeat(auto-fill,minmax(280px,1fr)))');

export default function ReportViewToggle({ view, onChange, color = 'var(--smc-ac)' }) {
  const btn = (v, label, icon) => (
    <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={view === v} title={`${label} view`}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', cursor: 'pointer',
        fontFamily: "'Share Tech Mono',monospace", fontSize: 9, letterSpacing: 1, border: 'none',
        background: view === v ? `color-mix(in srgb, ${color} 16%, transparent)` : 'transparent',
        color: view === v ? color : 'var(--smc-tx2, #94a3b8)' }}>
      <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>{icon}</span>{label.toUpperCase()}
    </button>
  );
  return (
    <div role="group" aria-label="Report layout"
      style={{ display: 'inline-flex', border: '1px solid var(--smc-bd, #334155)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
      {btn('grid', 'Grid', '▦')}
      {btn('list', 'List', '☰')}
    </div>
  );
}
